"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Category } from "@repo/apis/browse-category-v1";
import { Link } from "@repo/next-utils/router";
import { useDragScroll } from "@repo/ui/useDragScroll";
import { useHorizontalOverflow } from "@repo/ui/useHorizontalOverflow";
import { TYPOGRAPHY_LABEL3 } from "@/theme/topshop/typography/typography-constants";
import { formatCategoryPillUrl } from "@/utils/format-url";
import classes from "./category-pills.module.css";

type CategoryPillsProps = {
  category?: Category | null;
};

export function CategoryPills({ category }: CategoryPillsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasOverflow = useHorizontalOverflow(containerRef);
  const dragScrollRef = useDragScroll() as {
    current: HTMLDivElement | null;
  };
  const [scrollPosition, setScrollPosition] = useState({
    isStart: true,
    isEnd: false,
  });

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const handleScroll = () => {
      const isStart = element.scrollLeft <= 0;
      const isEnd =
        Math.ceil(element.scrollLeft + element.clientWidth) >=
        element.scrollWidth;
      setScrollPosition({ isStart, isEnd });
    };

    handleScroll();
    element.addEventListener("scroll", handleScroll);
    return () => element.removeEventListener("scroll", handleScroll);
  }, []);

  const { categories } = category?.categories || { categories: [] };

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      if (containerRef.current !== node) {
        (containerRef as { current: HTMLDivElement | null }).current = node;
      }
      if (dragScrollRef.current !== node) {
        dragScrollRef.current = node;
      }
    },
    [dragScrollRef],
  );

  if (!categories || categories.length === 0) return null;

  const activeCategoriesWithLinks = categories.filter(
    (item) => item.isActive && item.linkUrl,
  );

  if (activeCategoriesWithLinks.length === 0) return null;

  const { isStart, isEnd } = scrollPosition;

  // Temporary while we wait for the API to be updated
  const determineFormatCategoryPillUrl = (url: string) => {
    if (url.includes("/cat/")) {
      return formatCategoryPillUrl(url);
    }
    return url;
  };

  return (
    <div
      ref={setRefs}
      className={`
        ${classes.categoryPillsWrapper}
        ${hasOverflow ? classes.hasOverflow : ""}
        ${isStart ? classes.isStart : ""}
        ${isEnd ? classes.isEnd : ""}
      `}
    >
      <ul className={classes.categoryPills}>
        {activeCategoriesWithLinks.map((item, index) => (
          <li key={index} className={classes.categoryPillItem}>
            <Link
              href={determineFormatCategoryPillUrl(item.linkUrl as string)}
              className={`${TYPOGRAPHY_LABEL3} ${classes.categoryPill}`}
            >
              {item.friendlyName}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
